
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import ScrollToTop from '../../components/feature/ScrollToTop';
import Button from '../../components/base/Button';
import AnimatedSection from '../../components/base/AnimatedSection';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    budget: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        budget: '',
        message: ''
      });
    }, 2000);
  };

  const contactInfo = [
    {
      icon: 'ri-phone-fill',
      title: 'اتصل بنا',
      details: ['+966 50 123 4567', '+966 11 456 7890'],
      action: 'tel:+966501234567'
    },
    {
      icon: 'ri-mail-fill',
      title: 'راسلنا',
      details: ['info@kmediaproduction.com', 'sales@kmediaproduction.com'],
      action: 'mailto:info@kmediaproduction.com'
    },
    {
      icon: 'ri-map-pin-fill',
      title: 'زورنا',
      details: ['الرياض، حي الملز', 'طريق الملك فهد، مبنى رقم 123'],
      action: 'https://maps.google.com'
    },
    {
      icon: 'ri-time-fill',
      title: 'أوقات العمل',
      details: ['الأحد - الخميس: 9:00 ص - 6:00 م', 'الجمعة - السبت: مغلق'],
      action: null
    }
  ];

  const services = [
    'تصوير وإنتاج الفيديو',
    'الموشن جرافيك',
    'التسجيل الصوتي',
    'المونتاج والإخراج',
    'التصوير الفوتوغرافي',
    'البث المباشر',
    'أخرى'
  ];

  const budgetRanges = [
    'أقل من 10,000 ريال',
    '10,000 - 25,000 ريال',
    '25,000 - 50,000 ريال',
    '50,000 - 100,000 ريال',
    'أكثر من 100,000 ريال'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative pt-20 pb-32 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('https://public.readdy.ai/ai/img_res/56912eac2ee9d6b27eddd24cc93c29bc.jpg')`
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <AnimatedSection animation="fadeIn">
            <h1 className="text-5xl md:text-7xl font-bold mb-6">تواصل معنا</h1>
            <p className="text-xl md:text-2xl max-w-4xl mx-auto leading-relaxed">
              نحن هنا للإجابة على استفساراتكم ومساعدتكم في تحقيق رؤيتكم الإعلامية
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="slideUp" className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">طرق التواصل</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              تواصل معنا بالطريقة التي تناسبك
            </p>
          </AnimatedSection>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <AnimatedSection 
                key={index} 
                animation="slideUp" 
                delay={index * 100}
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-8 text-center group"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${info.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{info.title}</h3>
                <div className="space-y-2">
                  {info.details.map((detail, idx) => (
                    <p key={idx} className="text-gray-600">
                      {info.action ? (
                        <a href={info.action} className="hover:text-yellow-600 transition-colors duration-300">
                          {detail}
                        </a>
                      ) : (
                        detail
                      )}
                    </p>
                  ))}
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <AnimatedSection animation="slideRight">
              <div className="bg-gray-50 rounded-2xl p-8">
                <h3 className="text-3xl font-bold text-gray-900 mb-6">أرسل لنا رسالة</h3>
                <p className="text-gray-600 mb-8">
                  املأ النموذج أدناه وسنتواصل معك في أقرب وقت ممكن
                </p>
                
                {submitStatus === 'success' && (
                  <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-6">
                    تم إرسال رسالتك بنجاح! سنتواصل معك قريباً.
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">الاسم الكامل *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-300 text-sm"
                        placeholder="أدخل اسمك الكامل"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">البريد الإلكتروني *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-300 text-sm"
                        placeholder="example@email.com"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">رقم الهاتف *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-300 text-sm"
                        placeholder="+966 50 123 4567"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">اسم الشركة</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-300 text-sm"
                        placeholder="اسم شركتك (اختياري)"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">نوع الخدمة المطلوبة</label>
                      <div className="relative">
                        <select
                          name="service"
                          value={formData.service}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-300 text-sm appearance-none pr-8"
                        >
                          <option value="">اختر الخدمة</option>
                          {services.map((service, idx) => (
                            <option key={idx} value={service}>{service}</option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <i className="ri-arrow-down-s-line text-gray-400"></i>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">الميزانية المتوقعة</label>
                      <div className="relative">
                        <select
                          name="budget"
                          value={formData.budget}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-300 text-sm appearance-none pr-8"
                        >
                          <option value="">اختر النطاق</option>
                          {budgetRanges.map((range, idx) => (
                            <option key={idx} value={range}>{range}</option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <i className="ri-arrow-down-s-line text-gray-400"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">تفاصيل المشروع *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-300 text-sm resize-none"
                      placeholder="اكتب تفاصيل مشروعك هنا... (حد أقصى 500 حرف)"
                    ></textarea>
                    <div className="text-right text-sm text-gray-500 mt-1">
                      {formData.message.length}/500
                    </div>
                  </div>
                  
                  <Button 
                    variant="primary" 
                    size="lg" 
                    className="w-full"
                    onClick={() => {}}
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        جاري الإرسال...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-fill mr-2"></i>
                        إرسال الرسالة
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </AnimatedSection>
            
            {/* Map & Additional Info */}
            <AnimatedSection animation="slideLeft">
              <div className="space-y-8">
                <div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-6">موقعنا</h3>
                  <div className="bg-gray-200 rounded-2xl overflow-hidden h-80">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3624.397!2d46.686!3d24.7136!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjTCsDQyJzQ5LjAiTiA0NsKwNDEnMDkuNiJF!5e0!3m2!1sen!2ssa!4v1234567890"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="موقع K Media Production"
                    ></iframe>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-yellow-600 to-red-600 rounded-2xl p-8 text-white">
                  <h4 className="text-2xl font-bold mb-4">هل تحتاج استشارة سريعة؟</h4>
                  <p className="mb-6 leading-relaxed">
                    تواصل معنا مباشرة عبر الواتساب للحصول على استشارة فورية ومجانية
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <a
                      href="https://wa.me/966501234567"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-medium transition-all duration-300 flex items-center justify-center whitespace-nowrap"
                    >
                      <i className="ri-whatsapp-fill mr-2"></i>
                      واتساب
                    </a>
                    <a
                      href="tel:+966501234567"
                      className="bg-white text-gray-900 hover:bg-gray-100 px-6 py-3 rounded-lg font-medium transition-all duration-300 flex items-center justify-center whitespace-nowrap"
                    >
                      <i className="ri-phone-fill mr-2"></i>
                      اتصال مباشر
                    </a>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="slideUp" className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">الأسئلة الشائعة</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              إجابات على أكثر الأسئلة شيوعاً حول خدماتنا
            </p>
          </AnimatedSection>
          
          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                question: 'كم تستغرق مدة إنتاج الفيديو؟',
                answer: 'تختلف مدة الإنتاج حسب نوع وحجم المشروع، لكن عادة ما تتراوح بين أسبوعين إلى شهرين للمشاريع المتوسطة.'
              },
              {
                question: 'هل تقدمون خدمات التصوير خارج الرياض؟',
                answer: 'نعم، نقدم خدماتنا في جميع أنحاء المملكة العربية السعودية ودول الخليج العربي.'
              },
              {
                question: 'ما هي طرق الدفع المتاحة؟',
                answer: 'نقبل الدفع عبر التحويل البنكي، الشيكات، والدفع الإلكتروني. كما نوفر خطط دفع مرنة للمشاريع الكبيرة.'
              },
              {
                question: 'هل يمكنني الحصول على عينة من أعمالكم؟',
                answer: 'بالطبع! يمكنك زيارة معرض أعمالنا على الموقع أو طلب عينات مخصصة لنوع الخدمة التي تحتاجها.'
              }
            ].map((faq, index) => (
              <AnimatedSection 
                key={index} 
                animation="slideUp" 
                delay={index * 100}
                className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300"
              >
                <h4 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h4>
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
      <ScrollToTop />
    </div>
  );
}
